import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Mail, 
  Lock, 
  User, 
  Eye, 
  EyeOff,
  ArrowLeft,
  Shield,
  KeyRound,
  Crown,
  Check,
  CreditCard,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Colorful brain logo
const BRAIN_LOGO = "https://customer-assets.emergentagent.com/job_e95fcea1-1d05-419a-a729-ac97ff58a502/artifacts/1u3r91fj_logo%20no%20back_Image_Jan_4__2026_at_02_55_25_PM-removebg-preview.png";

export default function AuthPage({ initialMode = "login" }) {
  const navigate = useNavigate();
  const { login, register, isAuthenticated, isPremium, getSecurityQuestion, resetPassword, refreshUser } = useAuth();
  
  const [mode, setMode] = useState(initialMode); // login, register, forgot, reset, subscribe
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  
  // Form fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [userSecurityQuestion, setUserSecurityQuestion] = useState("");

  useEffect(() => {
    // If user is authenticated and premium, redirect to quizzes
    if (isAuthenticated && isPremium) {
      navigate("/quizzes");
    } else if (isAuthenticated && !isPremium) {
      // Authenticated but not premium - show subscribe
      setMode("subscribe");
    } else if (!isAuthenticated && initialMode === "subscribe") {
      // Not authenticated but trying to subscribe - show login first
      setMode("login");
    }
  }, [isAuthenticated, isPremium, navigate, initialMode]);

  useEffect(() => {
    // Fetch security questions
    const fetchQuestions = async () => {
      try {
        const response = await axios.get(`${API}/auth/security-questions`);
        setSecurityQuestions(response.data.questions);
      } catch (error) {
        console.error("Failed to fetch security questions");
      }
    };
    fetchQuestions();
  }, []);

  const handleSubscribe = async () => {
    // Set loading immediately for instant feedback
    setSubscriptionLoading(true);
    
    try {
      // Get the current origin for redirect URLs
      const originUrl = window.location.origin;
      const token = localStorage.getItem("token");
      
      if (!token) {
        toast.error("Please log in first to subscribe");
        setMode("login");
        setSubscriptionLoading(false);
        return;
      }
      
      const response = await axios.post(`${API}/stripe/create-checkout`, {
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Failed to create checkout session");
        setSubscriptionLoading(false);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Subscription failed";
      toast.error(errorMsg);
      setSubscriptionLoading(false);
    }
    // Note: Don't reset loading on success since we're redirecting
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await login(email, password);
      toast.success("Welcome back!");
      // Check if user is premium
      if (result.user?.is_premium) {
        navigate("/quizzes");
      } else {
        setMode("subscribe");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (!securityQuestion) {
      toast.error("Please select a security question");
      return;
    }
    
    if (!securityAnswer.trim()) {
      toast.error("Please provide a security answer");
      return;
    }
    
    setLoading(true);
    
    try {
      await register({
        email,
        password,
        name,
        security_question: securityQuestion,
        security_answer: securityAnswer
      });
      toast.success("Account created! Now subscribe to access quizzes.");
      setMode("subscribe");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGetSecurityQuestion = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const question = await getSecurityQuestion(email);
      setUserSecurityQuestion(question);
      setMode("reset");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Email not found");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      await resetPassword(email, securityAnswer, newPassword);
      toast.success("Password reset successful! Please login.");
      setMode("login");
      setEmail("");
      setSecurityAnswer("");
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setName("");
    setSecurityQuestion("");
    setSecurityAnswer("");
    setNewPassword("");
    setUserSecurityQuestion("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-6">
      <motion.div 
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl overflow-hidden shadow-lg">
            <img src={BRAIN_LOGO} alt="ABA Prep Lab" className="w-full h-full object-cover" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">ABA Prep Lab</h1>
          <p className="text-slate-600">BCBA Exam Preparation</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {/* Back button for forgot/reset modes */}
          {(mode === "forgot" || mode === "reset") && (
            <button 
              onClick={() => { setMode("login"); resetForm(); }}
              className="flex items-center gap-2 text-slate-600 hover:text-teal-600 mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </button>
          )}

          {/* Back button for subscribe mode (only if coming from registration, not login) */}
          {mode === "subscribe" && !isPremium && (
            <div className="mb-4 text-center">
              <p className="text-sm text-slate-500">
                Logged in as <span className="font-medium text-slate-700">{email || "user"}</span>
              </p>
            </div>
          )}

          {/* Login Form */}
          {mode === "login" && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-slate-900">Welcome Back</h2>
                <p className="text-slate-600 text-sm">Sign in to continue your progress</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={() => { setMode("forgot"); resetForm(); }}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium"
              >
                Forgot password?
              </button>

              <Button 
                type="submit" 
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>

              <p className="text-center text-slate-600 text-sm">
                Don&apos;t have an account?{" "}
                <button
                  type="button"
                  onClick={() => { setMode("register"); resetForm(); }}
                  className="text-teal-600 hover:text-teal-700 font-semibold"
                >
                  Create Account
                </button>
              </p>
            </form>
          )}

          {/* Register Form */}
          {mode === "register" && (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-slate-900">Create Account</h2>
                <p className="text-slate-600 text-sm">Start your BCBA prep journey</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700 font-medium">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="register-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-email" className="text-slate-700 font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="reg-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-password" className="text-slate-700 font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="reg-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-slate-700 font-medium">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700 font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Security Question
                </Label>
                <Select value={securityQuestion} onValueChange={setSecurityQuestion}>
                  <SelectTrigger data-testid="register-security-question">
                    <SelectValue placeholder="Select a security question" />
                  </SelectTrigger>
                  <SelectContent>
                    {securityQuestions.map((q, i) => (
                      <SelectItem key={i} value={q}>{q}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="security-answer" className="text-slate-700 font-medium">Security Answer</Label>
                <Input
                  id="security-answer"
                  type="text"
                  placeholder="Your answer (for password recovery)"
                  value={securityAnswer}
                  onChange={(e) => setSecurityAnswer(e.target.value)}
                  required
                  data-testid="register-security-answer"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </Button>

              <p className="text-center text-slate-600 text-sm">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => { setMode("login"); resetForm(); }}
                  className="text-teal-600 hover:text-teal-700 font-semibold"
                >
                  Sign In
                </button>
              </p>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === "forgot" && (
            <form onSubmit={handleGetSecurityQuestion} className="space-y-5">
              <div className="text-center mb-6">
                <KeyRound className="w-12 h-12 mx-auto text-teal-500 mb-3" />
                <h2 className="text-xl font-bold text-slate-900">Reset Password</h2>
                <p className="text-slate-600 text-sm">Enter your email to get your security question</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-slate-700 font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="forgot-email"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
                disabled={loading}
                data-testid="forgot-submit"
              >
                {loading ? "Looking up..." : "Continue"}
              </Button>
            </form>
          )}

          {/* Reset Password Form */}
          {mode === "reset" && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="text-center mb-6">
                <Shield className="w-12 h-12 mx-auto text-teal-500 mb-3" />
                <h2 className="text-xl font-bold text-slate-900">Answer Security Question</h2>
              </div>

              <div className="p-4 bg-teal-50 rounded-xl border border-teal-200">
                <p className="text-sm text-teal-800 font-medium">{userSecurityQuestion}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reset-answer" className="text-slate-700 font-medium">Your Answer</Label>
                <Input
                  id="reset-answer"
                  type="text"
                  placeholder="Enter your security answer"
                  value={securityAnswer}
                  onChange={(e) => setSecurityAnswer(e.target.value)}
                  required
                  data-testid="reset-answer"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-slate-700 font-medium">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 6 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
                disabled={loading}
                data-testid="reset-submit"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          )}

          {/* Subscribe Form */}
          {mode === "subscribe" && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Crown className="w-12 h-12 mx-auto text-amber-500 mb-3" />
                <h2 className="text-xl font-bold text-slate-900">Subscribe to Access</h2>
                <p className="text-slate-600 text-sm">Unlock all quizzes and mock exams</p>
              </div>

              {/* Pricing Card */}
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 border border-teal-200">
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="text-4xl font-bold text-slate-900">$7.00</span>
                  <span className="text-slate-600">/month</span>
                </div>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-teal-500" />
                    <span>Access to all 530+ practice questions</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-teal-500" />
                    <span>4 full-length mock exams (185 questions each)</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-teal-500" />
                    <span>Option to remove time limits</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-teal-500" />
                    <span>AI-powered detailed explanations</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-teal-500" />
                    <span>Progress tracking & analytics</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-teal-500" />
                    <span>Weak areas identification</span>
                  </li>
                </ul>

                {/* Stripe Checkout Button */}
                <Button
                  onClick={handleSubscribe}
                  disabled={subscriptionLoading}
                  className={`w-full py-6 font-semibold flex items-center justify-center gap-2 transition-all ${
                    subscriptionLoading 
                      ? "bg-[#635bff]/80 cursor-wait" 
                      : "bg-[#635bff] hover:bg-[#5449eb]"
                  } text-white`}
                  data-testid="stripe-subscribe-btn"
                >
                  {subscriptionLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Redirecting to Stripe...</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Subscribe - $7.00/month
                    </>
                  )}
                </Button>
              </div>

              <p className="text-center text-xs text-slate-500">
                Cancel anytime. Secure payment via Stripe.
              </p>
            </div>
          )}
        </div>

      </motion.div>
    </div>
  );
}
